import type { SessionResponse } from './contracts'
import { ApiError, errorCodes, toApiError } from './problem'

/**
 * Erişim token'ı — yalnızca bellekte.
 *
 * Asla localStorage/sessionStorage'a yazılmaz (`docs/standards/frontend-standards.md §7`): bir
 * script'in okuyabildiğini bir XSS açığı dışarı sızdırabilir. Oturumun iptal edilebilir yarısı,
 * httpOnly olduğu için bu koda — bu kodun hatalarına da — görünmeyen refresh cookie'sidir.
 */
let accessToken: string | null = null

/** Oturum kalıcı olarak bittiğinde çağrılır; uygulama kullanıcıyı giriş ekranına gönderebilsin diye. */
let onSessionLost: (() => void) | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

export function setSessionLostHandler(handler: (() => void) | null): void {
  onSessionLost = handler
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  readonly body?: unknown

  /**
   * Dosya yükleyen isteklerin gövdesi.
   *
   * `body`'den ayrı bir alan, çünkü ikisi farklı davranıyor: bu JSON'a çevrilmez ve
   * `Content-Type` **yazılmaz** — sınır dizesini (boundary) tarayıcı üretir ve elle bir başlık
   * koymak onu bozar. Ayrı tutulması, 401 sonrası tekrar denemenin de bu isteklerde çalışması
   * demek: `FormData` yeniden gönderilebilir bir gövdedir.
   */
  readonly formData?: FormData
  readonly signal?: AbortSignal
  /**
   * Yenile-ve-tekrar dene adımını atlar. Kimlik uçlarının kendisinde kullanılır: başarısız bir
   * giriş, süresi dolmuş oturum değil yanlış şifredir; onu yenilemeye çalışmak anlamsız olurdu.
   */
  readonly anonymous?: boolean
}

/**
 * API'yi çağırır ve ayrıştırılmış gövdeyi döndürür; başarısızlıkta {@link ApiError} fırlatır.
 *
 * 401'de bir kez yeniler ve isteği tekrarlar. Kısa ömürlü erişim token'ının bütün amacı budur:
 * kullanıcı iş ortasındayken süresi dolar, oturum ise refresh cookie'si ondan uzun yaşadığı için
 * devam eder.
 *
 * Ama yalnız **oturum** 401'inde. Sunucu, gönderilen bir sırrın reddini `INVALID_CREDENTIALS` ile
 * ayrı bildiriyor ve onu yenilemek yanlış olurdu: oturum sapasağlam olduğu için yenileme başarılı
 * olur ve istemci aynı yanlış şifreyi ikinci kez gönderir — kullanıcı bir kez denemişken sunucu
 * iki deneme sayar, kilitlenme eşiği yarı yarıya erken dolar (`docs/spec/03-security.md §1.1`).
 * Ayrım burada, tek yerde yapılıyor; çağrı yerlerinin kendi ucunun 401'ini tanıması gerekmiyor.
 */
/**
 * Dosya yükler ve sunucunun yanıtını çözer.
 *
 * `apiRequest`'in üstünde ince bir sarmalayıcı, ayrı bir yol değil: oturum yenileme, hata zarfı ve
 * correlation id aynı yerde kalsın diye.
 */
export function apiUpload<TResponse>(path: string, form: FormData): Promise<TResponse> {
  return apiRequest<TResponse>(path, { method: 'POST', formData: form })
}

export async function apiRequest<TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const response = await send(path, options)

  if (response.status === 401 && !options.anonymous) {
    // Gövde bir kez okunabilir; kararı vermek için zaten gereken zarfı burada çözüp saklıyoruz.
    const error = await toApiError(response)

    if (error.errorCode === errorCodes.invalidCredentials) {
      throw error
    }

    const refreshed = await refreshSession()

    if (refreshed === null) {
      onSessionLost?.()
      throw error
    }

    const retried = await send(path, options)

    if (!retried.ok) {
      throw await toApiError(retried)
    }

    return parse<TResponse>(retried)
  }

  if (!response.ok) {
    throw await toApiError(response)
  }

  return parse<TResponse>(response)
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {
    // Bu isteği sunucunun log satırlarına bağlar. Sunucu kendisine verileni doğrular; başlık yoksa
    // ya da bozuksa kendi değerini üretir (`docs/spec/11-logging-observability.md §3`).
    'X-Correlation-Id': crypto.randomUUID(),

    // `docs/spec/03-security.md §6`, SameSite=Strict cookie'nin üstüne durum değiştiren uçlarda
    // özel bir başlık istiyor: siteler arası bir form böyle bir başlığı ayarlayamaz. Sunucu bunu
    // henüz aramıyor — şimdiden göndermek, aramaya başladığı gün bu istemcinin hazır olması demek.
    'X-Requested-With': 'XMLHttpRequest',
  }

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  if (accessToken !== null && !options.anonymous) {
    headers.Authorization = `Bearer ${accessToken}`
  }

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,

    // Refresh cookie'si yalnızca bunun üstünde taşınır.
    credentials: 'include',
  }

  // Yalnız varsa atanır: `exactOptionalPropertyTypes`, "hiç yok" ile "açıkça undefined" arasına
  // çizgi çeker ve `RequestInit` ikincisini kabul etmez.
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body)
  }

  if (options.formData !== undefined) {
    init.body = options.formData
  }

  if (options.signal !== undefined) {
    init.signal = options.signal
  }

  return fetch(path, init)
}

/**
 * Gövdesiz bir yanıtı durum koduna veya başlığa değil, gövdenin kendisine bakarak tanır.
 *
 * Önceki sürüm yalnız `204`'ü ve `Content-Length: 0`'ı özel durum sayıyordu — ama `202 Accepted`
 * gövdesiz de dönebilir (`POST public/account-requests`, `docs/spec/06-api-spec.md §Public`) ve
 * sunucu/ara katmanların hepsi bu durumda `Content-Length` başlığını yazmayabilir. Boş metni
 * `JSON.parse` etmeye çalışmak, "gövde yok"u bir ayrıştırma hatasına çeviriyordu.
 */
async function parse<TResponse>(response: Response): Promise<TResponse> {
  const text = await response.text()

  if (text === '') {
    return undefined as TResponse
  }

  return JSON.parse(text) as TResponse
}

/**
 * Devam eden yenileme; ihtiyacı olan her çağıran aynısını paylaşır.
 *
 * Bu bir iyileştirme değil, taşıyıcı bir ayrıntı. Refresh token'ları rotasyonludur ve sunucu aynı
 * token'ın ikinci kullanımını hırsızlık sayıp **tüm aileyi iptal eder** (`03-security §1.1`).
 * Dolayısıyla birlikte süresi dolan üç istek ayrı ayrı yenileseydi bu yalnızca israf olmazdı:
 * ikisi çoktan döndürülmüş bir token sunar ve kullanıcı oturumdan düşerdi. Hepsi tek bir sözü bekler.
 *
 * **Sözün tek olması, tek yerde olmasına bağlı.** Bu koruma bir süre yalnız bu dosyanın çağrılarını
 * kapsadı: `AuthProvider` açılışta oturumu geri yüklerken kendi `fetch`'ini yapıyordu, yani her
 * sayfa yüklemesinde iki bağımsız yenileme başlıyordu. Çoğu zaman sıralı gidip ikisi de başarılı
 * oluyordu; çakıştıklarında ikincisi tüketilmiş token'ı sunuyor ve yukarıdaki iptal tam olarak
 * gerçekleşiyordu. Üretim derlemesinde ölçüldü (2026-08-13): **sekiz yüklemenin biri** kullanıcıyı
 * giriş ekranına atıyordu. O yüzden `refreshSession` artık dışa açık ve **oturumun tamamını**
 * döndürüyor — `AuthProvider`'ın kullanıcıyı da alabilmesi için; ayrı bir yol açmaya sebebi kalmasın.
 */
let refreshInFlight: Promise<SessionResponse | null> | null = null

export function refreshSession(): Promise<SessionResponse | null> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null
  })

  return refreshInFlight
}

async function performRefresh(): Promise<SessionResponse | null> {
  try {
    const response = await fetch('/app/v1/auth/refresh', {
      method: 'POST',
      headers: {
        'X-Correlation-Id': crypto.randomUUID(),
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      setAccessToken(null)

      return null
    }

    const session = (await response.json()) as Partial<SessionResponse>

    if (typeof session.accessToken !== 'string') {
      setAccessToken(null)

      return null
    }

    setAccessToken(session.accessToken)

    return session as SessionResponse
  } catch {
    // Reddedilmiş token değil, ağ hatası. "Yenilenemedi" sayılır ki çağıran asıl hatayı göstersin;
    // oturum temizlenmez, çünkü bağlantı geri geldiğinde token hâlâ geçerli olabilir.
    return null
  }
}

export { ApiError }
